import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Optional local type for variant metadata
export interface WishlistVariant {
  id?: string; // variantId (size+color hash)
  size?: string;
  color?: string;
  colorHex?: string;
  priceModifier?: number;
  sku?: string;
}

export interface WishlistItem {
  id: string; // productId
  name: string;
  // Final price to display in wishlist context (basePrice + priceModifier)
  price: number;
  basePrice?: number;
  priceModifier?: number;
  image: string;
  images?: string[];
  category: string;
  variant?: WishlistVariant;

  // internal composite key to de-duplicate by product+variant
  _key?: string;
}

interface WishlistState {
  items: WishlistItem[];
}

const initialState: WishlistState = {
  items: [],
};

// Build a composite key for product + variant
function buildKey(id: string, variant?: WishlistVariant): string {
  const variantKey =
    variant?.id ||
    (variant?.size || '') + '::' + (variant?.color || '');
  return variantKey && variantKey !== '::' ? `${id}__${variantKey}` : id;
}

// Accepted payload when adding to wishlist.
// Keep it broad for backward compatibility with existing calls.
type AddToWishlistPayload = {
  id: string;
  name: string;
  price: number; // final price for display
  basePrice?: number;
  priceModifier?: number;
  image?: string;
  images?: string[];
  category?: string;
  variant?: WishlistVariant;
  // Legacy fields if callers pass size/color at top-level
  size?: string;
  color?: string;
  colorHex?: string;
};

type RemoveFromWishlistPayload =
  | string
  | { id: string; variantId?: string; size?: string; color?: string };

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    setWishlist: (state, action: PayloadAction<WishlistItem[]>) => {
      // Ensure keys are normalized
      state.items = (action.payload || []).map((it) => ({
        ...it,
        _key: buildKey(it.id, it.variant),
      }));
    },
    addToWishlist: (state, action: PayloadAction<AddToWishlistPayload>) => {
      const p = action.payload;
      const variant: WishlistVariant | undefined =
        p.variant ||
        (p.size || p.color
          ? {
              size: p.size,
              color: p.color,
              colorHex: p.colorHex,
              priceModifier: p.priceModifier,
            }
          : undefined);

      const key = buildKey(p.id, variant);

      const exists = state.items.find((item) => item._key === key);
      if (!exists) {
        state.items.push({
          id: p.id,
          name: p.name,
          price: Number(p.price),
          basePrice: p.basePrice ?? Number(p.price) - Number(p.priceModifier || 0),
          priceModifier: p.priceModifier,
          image: p.image || p.images?.[0] || '',
          images: p.images,
          category: p.category || 'furniture',
          variant,
          _key: key,
        });
      }
    },
    removeFromWishlist: (state, action: PayloadAction<RemoveFromWishlistPayload>) => {
      // Support legacy string (productId) or object with variant specificity
      const payload = action.payload;
      if (typeof payload === 'string') {
        // Remove all entries for that product id
        state.items = state.items.filter((item) => item.id !== payload);
      } else {
        const key = payload.variantId
          ? `${payload.id}__${payload.variantId}`
          : buildKey(payload.id, { size: payload.size, color: payload.color });
        state.items = state.items.filter((item) => item._key !== key);
      }
    },
    clearWishlist: (state) => {
      state.items = [];
    },
  },
});

export const { setWishlist, addToWishlist, removeFromWishlist, clearWishlist } = wishlistSlice.actions;
export default wishlistSlice.reducer;